import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { AxiosError } from "axios";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import useTenant from "../../hooks/useTenant";
import OperationTypes from "../../types";
import CheckInOutWrapper from "../common/CheckInOutWrapper";
import FormInput from "../common/FormInput";
import VehicleMaintanenceService from "./VehicleMaintanenceService";

const VehicleMaintanence = () => {
  const { tenant, setLoading, userLocations } = useContext(ContextOperation);
  const { globalData } = useContext(GlobalContext);

  const t = useOperationTranslation();
  const snackbar = useSnackbar();

  const [vehicle, setVehicle] = useState<OperationTypes.Vehicle | null>(null);

  const { TenantAutocomplete } = useTenant();

  const form = useForm<{plate: string}>();

  const onSubmit = (data: {plate: string}) => {
    setLoading(true)
    VehicleMaintanenceService.get.vehicleByPlate(tenant, data.plate?.trim()).then(
      response => {
        setLoading(false);

        if (response.physicalLocationId === null || response.physicalLocationId === undefined) {
          snackbar.show("warning", t("vehicleMaintenance.warningVehicleTransfering"));
          return;
        }
        
        if (!userLocations.map(loc => loc.id).includes(response.physicalLocationId)) {
          snackbar.show("warning", t("vehicleMaintenance.warningNotInLocation").replace("#textElement", globalData.user.username));
          return;
        }

        setVehicle(response);
      },
      (error: AxiosError) => {
        setLoading(false);

        if (error?.response?.status === 404) {
          snackbar.show("warning", t("vehicleMaintenance.errorNotFound"));
        } else {
          snackbar.show("error", t("vehicleMaintenance.errorGeneral"));
        }
      }
    )
  }

  const onConfirmCheck = () => {
    if (vehicle?.plate) {
      snackbar.show(
        "success",
        t("vehicleMaintenance.success").replace(
          "#plate",
          vehicle?.plate
        )
      );
    }
    setVehicle(null);
    form.reset();
  }

  return (
    <Mui.Box>
      {!vehicle && (
        <form id="vehicle-check-search" onSubmit={form.handleSubmit(onSubmit)}>
          <Mui.Box height="calc(100vh - 48px - 88px - 32px)" padding={{ md: 2, xs:0 }}>
            <Mui.Card style={{ height: "100%" }}>
              <Mui.CardContent style={{ height: "100%" }}>
                <Mui.Stack height="calc(100% - 32px)" overflow="auto">
                  <Mui.Typography variant="h4" textAlign="center" mb={2}>
                    {t("vehicleMaintenance.cardTitle")}
                  </Mui.Typography>
                  
                  <Mui.Typography variant="body" whiteSpace="pre-line" textAlign="center" mb="80px">
                    {t("vehicleMaintenance.cardSubtitle")}
                  </Mui.Typography>
                  
                  <Mui.Grid container  columns={12} p="0px 16px" mb="25px" spacing={2}>
                    {TenantAutocomplete}

                    <Mui.Grid 
                      item 
                      xl={TenantAutocomplete ? 9 : 12} 
                      lg={TenantAutocomplete ? 9 : 12} 
                      md={TenantAutocomplete ? 6 : 12} 
                      xs={12}
                    >
                      <FormInput
                        name="plate"
                        type="text"
                        label={t("vehicleMaintenance.inputPlateLabel")}
                        placeholder={t("vehicleMaintenance.inputPlatePlaceholder")}
                        fullWidth
                        formInstance={form}
                      />
                    </Mui.Grid>
                  </Mui.Grid>

                  <Mui.Stack direction="row" alignItems="center" justifyContent="center" mb="25px">
                    <Mui.Typography variant="body">
                    {t("vehicleMaintenance.cardQrCode")}
                    </Mui.Typography>
                    <MuiIcons.InfoOutlined style={{ marginLeft: "5px" }} />
                  </Mui.Stack>

                  <Mui.Button variant="contained" color="accent" disabled={true} style={{ margin: "0px 16px"}}>
                    {t("vehicleMaintenance.btnQrCode")} <MuiIcons.QrCode />
                  </Mui.Button>

                  <Mui.Button variant="contained" color="accent" type="submit" style={{ margin: "auto 16px 0px 16px" }}>
                    {t("vehicleMaintenance.btnContinue")}
                  </Mui.Button>
                </Mui.Stack>
              </Mui.CardContent>
            </Mui.Card>
          </Mui.Box>
        </form>
      )}

      {vehicle && (
        <CheckInOutWrapper
          brandDescription={vehicle.model.brand.descr}
          callbackButtonLabel={t("vehicleMaintenance.btnConfirm")}
          currentBatteryPercentage={vehicle.currentElectricTankFilling}
          currentFuelLevelEighths={vehicle.currentTermicTankFilling}
          currentKms={vehicle.kms}
          enginePowerGroupCode={vehicle.enginePowerCode}
          modelDescription={vehicle.model.descr}
          oprCallback={onConfirmCheck}
          plate={vehicle.plate}
          carClass={vehicle.carClass.descr}
          bypassDamagesEquipments={false}
		  enableCounterPart={false}
		  viewType='dataUpd'
        />
      )}
    </Mui.Box>
  )
}

export default VehicleMaintanence;